import {
  AssetCartItem,
  PropertyCartItem,
  EntityGroup
} from '../types/cart.types';

// ============================================
// Grouped Assets Type
// ============================================

export type GroupedAssets = {
  propertyUUID: string;
  propertyAddress: string;
  entityName?: string;
  photosetUUID?: string;
  items: AssetCartItem[];
  subtotal: number;
};

// ============================================
// Grouping Utilities
// ============================================

/**
 * Groups asset cart items by their propertyUUID.
 * Each group contains all asset items for a single property,
 * along with the address, entity name, and a subtotal.
 */
export function groupAssetsByProperty(
  assetItems: AssetCartItem[]
): GroupedAssets[] {
  const grouped = assetItems.reduce<Record<string, GroupedAssets>>(
    (acc, item) => {
      const key = item.propertyUUID;
      if (!acc[key]) {
        acc[key] = {
          propertyUUID: key,
          propertyAddress: item.propertyAddress,
          entityName: item.entityName,
          photosetUUID: item.photosetUUID,
          items: [],
          subtotal: 0
        };
      }
      acc[key].items.push(item);
      acc[key].subtotal += item.price;
      return acc;
    },
    {}
  );

  return Object.values(grouped);
}

/**
 * Groups property cart items by their entityUUID.
 * Each group contains all property items for a single entity,
 * along with the entity name and a subtotal.
 */
export function groupPropertyItemsByEntity(
  propertyItems: PropertyCartItem[]
): EntityGroup<PropertyCartItem>[] {
  const grouped = propertyItems.reduce<
    Record<string, EntityGroup<PropertyCartItem>>
  >((acc, item) => {
    const key = item.entityUUID || 'default';
    if (!acc[key]) {
      acc[key] = {
        entityUUID: item.entityUUID || '',
        entityName: item.entityName || '',
        items: [],
        subtotal: 0
      };
    }
    acc[key].items.push(item);
    acc[key].subtotal += item.price || 0;
    return acc;
  }, {});

  return Object.values(grouped);
}
